import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Box,
} from '@mui/material';
import { IconChevronDown16, IconGeneral } from '@shared/assets/images/svgs';
import CustomAlert from '@shared/components/alert/alert';
import { CampaignRetryFields } from '@shared/components/campaignRetryFields/CampaignRetryFields';
import CustomFormLabelV2 from '@shared/components/forms/theme-elements/CustomFormLabelV2';
import { CounterInput } from '@shared/components/counterInput/counterInput';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { ChangeRetryValueByButtonData } from '@shared/components/campaignRetryFields/CampaignRetryFields';
import { initialSchedule } from '@shared/constants/initialSchedule';
import { CallScheduleRequestDto, CampaignDto } from '@shared/services/apiService/apiService';
import { CampaignFieldsError } from '@shared/types/admin/campaign';
import { ScheduleSelect } from '@widgets/Popup/Schedule/ScheduleSelect';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { StyledErrorText } from '@shared/components/errorText';
import { CreateCampaignFormValue } from '@features/Admin/Campaign/model/type';
import { CampaignTypeEnum } from '@shared/constants/campaign.constants';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { RootState, useAppSelector } from '@shared/store';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { clampReservedLimit } from './reservedLimit.utils';

interface GeneralTabProps {
  campaign?: CampaignDto | null;
  actions?: React.ReactNode;
}

const GeneralTab: React.FC<GeneralTabProps> = ({ campaign, actions }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    control,
    formState: { errors: formStateErrors },
  } = useFormContext<CreateCampaignFormValue>();
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);

  const [campaignFieldError, setCampaignFieldError] = useState<CampaignFieldsError>({
    retryIntervalInMinutes: [],
    retryMaxNumber: [],
    retriesPerDay: [],
    editErrorMessage: '',
  });

  const [schedule, setSchedule] = useState<CallScheduleRequestDto>(initialSchedule);
  const [retryMaxNumberValue, setRetryMaxNumberValue] = useState('');
  const [retriesPerDayValue, setRetriesPerDayValue] = useState('');
  const [retryIntervalInMinutesValue, setRetryIntervalInMinutesValue] = useState('');

  useEffect(() => {
    if (campaign && campaign.callSchedule) {
      setRetryMaxNumberValue(campaign.retryMaxNumber?.toString() || '0');
      setRetriesPerDayValue(campaign.retriesPerDay?.toString() || '0');
      setRetryIntervalInMinutesValue(campaign.retryIntervalInMinutes?.toString() || '0');
      setSchedule(campaign.callSchedule);
    }
  }, [campaign]);

  const setCallSchedule = (
    value: CallScheduleRequestDto,
    onChange: (value: CallScheduleRequestDto) => void,
  ) => {
    setSchedule(value);
    onChange(value);
  };

  let fields = [
    {
      name: 'retryMaxNumber',
      label: t('campaignPage.overallRetryAttempt'),
      placeholder: t('campaignPage.overallRetryAttemptPlaceholder'),
      retryFieldError: campaignFieldError.retryMaxNumber || [],
      retryFieldValue: retryMaxNumberValue,
      setRetryFieldValue: setRetryMaxNumberValue,
    },
    {
      name: 'retriesPerDay',
      label: t('campaignPage.retryPerDay'),
      placeholder: t('campaignPage.retryPerDayPlaceholder'),
      retryFieldError: campaignFieldError.retriesPerDay || [],
      retryFieldValue: retriesPerDayValue,
      setRetryFieldValue: setRetriesPerDayValue,
    },
    {
      name: 'retryIntervalInMinutes',
      label: t('campaignPage.retryDelay'),
      placeholder: t('campaignPage.retryDelayPlaceholder'),
      retryFieldError: campaignFieldError.retryIntervalInMinutes || [],
      retryFieldValue: retryIntervalInMinutesValue,
      setRetryFieldValue: setRetryIntervalInMinutesValue,
    },
  ];

  return (
    <>
      <form style={{ width: '100%' }}>
        <Grid
          container
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 3 },
            boxSizing: 'border-box',
            flexDirection: 'column',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              marginBottom: '20px',
              minHeight: 36,
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minHeight: 36,
                'svg path': {
                  stroke: theme.palette.getContrastText(theme.palette.background.default),
                },
              }}>
              <IconGeneral />
              <Typography
                component="span"
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: '36px',
                  display: 'inline-block',
                }}>
                {t('campaignPage.general')}
              </Typography>
            </Box>
            {actions}
          </Box>
          <BorderedGrid item xs={10} sm={8}>
            <CustomFormLabelV2
              sx={{
                mt: 0,
              }}
              htmlFor="campaign-title"
              required>
              {t('campaignPage.campaignName')}
            </CustomFormLabelV2>
            <Controller
              control={control}
              name="title"
              disabled={currentUser?.role === UserRolesEnum.VIEWER}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomTextField
                  {...field}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e.target.value);
                  }}
                  id="campaign-title"
                  sx={{
                    ...(formStateErrors.title
                      ? {
                          '& fieldset': {
                            borderColor: `${theme.palette.error.main} !important`,
                          },
                        }
                      : {}),
                  }}
                  variant="outlined"
                  value={field.value}
                  required
                />
              )}
            />
            {formStateErrors.title && (
              <StyledErrorText>{t('adminMainPage.errors.fillTheField')}</StyledErrorText>
            )}
          </BorderedGrid>
          <BorderedGrid item xs={10} sm={8}>
            <CustomFormLabelV2 sx={{ mt: 0 }} htmlFor="campaign-reserved-limit">
              {t('campaignPage.reservedLimit')}
            </CustomFormLabelV2>
            <Grid container spacing={2}>
              <Grid item xs={3} sm={4}>
                <Controller
                  control={control}
                  name="reservedLimit"
                  disabled={currentUser?.role === UserRolesEnum.VIEWER}
                  rules={{ min: 1 }}
                  render={({ field }) => (
                    <CounterInput
                      id="campaign-reserved-limit"
                      retryFieldValue={
                        field.value === undefined || field.value === null ? '' : String(field.value)
                      }
                      fieldName="reservedLimit"
                      control={control}
                      isErrored={Boolean(formStateErrors.reservedLimit)}
                      min={1}
                      handleOnChangeRetryFieldInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const filteredValue = e.target.value.replace(/[^0-9]/g, '');
                        if (filteredValue === '') {
                          field.onChange(undefined);
                          return;
                        }
                        field.onChange(Math.max(1, parseInt(filteredValue, 10)));
                      }}
                      handleOnChangeRetryValueByButton={(data: ChangeRetryValueByButtonData) =>
                        field.onChange(clampReservedLimit(data.value))
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
            {formStateErrors.reservedLimit && (
              <StyledErrorText>{t('adminMainPage.errors.fillTheField')}</StyledErrorText>
            )}
          </BorderedGrid>
          {campaign?.type === CampaignTypeEnum.OUTBOUND && (
            <BorderedGrid item xs={10} sm={8}>
              <CampaignRetryFields fields={fields} setCampaignFieldError={setCampaignFieldError} />
            </BorderedGrid>
          )}
          {campaign?.type === CampaignTypeEnum.OUTBOUND && (
            <Grid item xs={10} sm={8} sx={{ pb: 2 }}>
              <Accordion
                defaultExpanded
                sx={{
                  boxShadow: 'unset',
                }}>
                <AccordionSummary
                  expandIcon={<IconChevronDown16 />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  sx={{
                    padding: '0px',
                    justifyContent: 'flex-start',
                    '& .MuiAccordionSummary-content': {
                      flexGrow: 'unset',
                    },
                    'svg path': {
                      stroke: theme.palette.primary.main,
                    },
                  }}>
                  <Typography
                    component="span"
                    sx={{
                      paddingRight: '6px',
                      color: theme.palette.primary.main,
                    }}>
                    {t('campaignPage.changeSchedule')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: '0px',
                    display: 'block',
                  }}>
                  <Controller
                    control={control}
                    name="callSchedule"
                    render={({ field }) => (
                      <ScheduleSelect
                        schedule={field.value ?? schedule}
                        setSchedule={(value) => setCallSchedule(value, field.onChange)}
                      />
                    )}
                  />
                  {formStateErrors.callSchedule && (
                    <StyledErrorText>{t('adminMainPage.errors.fillTheField')}</StyledErrorText>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
      </form>
      {campaignFieldError.editErrorMessage ? (
        <CustomAlert message={campaignFieldError.editErrorMessage} type={'error'} />
      ) : (
        ''
      )}
    </>
  );
};

export default GeneralTab;
